/**
 * Module: screens/creator/StatusScreen
 * Role: Step 8/8 — post-submit status polling (FTR-1106 + FTR-1131 chrome
 *       rewrite). Polls GET /verify_status every 30s:
 *         pending → spinner + «1–2 минуты + до 1–2 дней» copy
 *         approved → success + redirect to /tasks in 3s
 *         rejected → rejection_reason + retry button
 *         429 rate-limit (carried from wizard store) → wait-time + retry
 *       Back-pill on every non-approved branch closes the wizard to /profile
 *       (founder report 2026-06-07: any wizard surface must be escapable).
 *
 * Uses:
 *   - react-router-dom:useNavigate
 *   - @tanstack/react-query:useQuery
 *   - api/creator:getVerifyStatus
 *   - screens/creator/store:useWizardStore
 *   - screens/creator/_chrome/WizardShell
 *   - styles/tokens:P
 *   - components/atoms/Ic
 *
 * Used by: App.tsx route /creator-status
 * Glossary: ai/glossary/buyer.md
 *
 * ADR-023: no window.Telegram / platform literals
 * ADR-022 §7: no openExternal(BOT_LINK)
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getVerifyStatus, recheckOwnership } from '../../api/creator';
import { useWizardStore } from './store';
import { P, T } from '../../styles/tokens';
import { Ic } from '../../components/atoms/Ic';
import {
  WizardBody, WizardCtaBar, WizardFrame, WizardHeader, WizardStepStrip,
} from './_chrome/WizardShell';
import { rejectionReasonLabel } from './_labels';

const POLL_INTERVAL_MS = 30_000;

// BUG-1451 B1: статусы, которые действительно означают «работа идёт» — только они
// получают спиннер с обещанием «1–2 минуты». `needs_info` (ручная очередь оператора)
// и `banned` (терминальный) имеют собственные экраны ниже; всё незнакомое получает
// нейтральный экран без обещаний, а не вечное вращение.
// SYMMETRIC — sync with CreatorApplicationStatusEnum (miniapp/src/api/creator.ts)
// и CHECK-констрейнтом creator_applications.status.
const SPINNER_STATUSES = ['pending', 'pending_scraping', 'no_application'];

// TECH-1456: отвал распознавателя — наша проблема, а не ошибка человека.
// Ни причины, ни просьбы переделать, ни обещания «1–2 минуты».
// SYMMETRIC LIMIT (ADR-022) — те же слова живут в двух других рантаймах:
//   - src/domains/buyer/locales/ru.yaml:proofs.messages.pending_review (бот)
//   - src/domains/flow_engine/checkpoint_runner.py:_MANUAL_REVIEW_NOTICE (флоу)
// Гейт: tests/unit/domains/flow_engine/test_checkpoint_escalation_copy.py
const MANUAL_REVIEW_NOTICE =
  'Обычно занимает от нескольких минут до нескольких часов — мы напишем, как только проверим.';

function formatRetryAfter(retryAtMs: number): string {
  const diff = retryAtMs - Date.now();
  if (diff <= 0) return 'сейчас';
  const hours = Math.ceil(diff / (1000 * 60 * 60));
  if (hours >= 1) return `${hours} ${hours === 1 ? 'час' : hours < 5 ? 'часа' : 'часов'}`;
  const mins = Math.ceil(diff / (1000 * 60));
  return `${mins} ${mins === 1 ? 'минуту' : mins < 5 ? 'минуты' : 'минут'}`;
}

export default function StatusScreen() {
  const navigate = useNavigate();
  const { draft, resetDraft } = useWizardStore();

  const isRateLimited = draft.rateLimitRetryAt !== null;
  const retryAfterMs = draft.rateLimitRetryAt;

  const { data: status } = useQuery({
    queryKey: ['creator', 'verify_status'],
    queryFn: getVerifyStatus,
    refetchInterval: POLL_INTERVAL_MS,
    enabled: !isRateLimited,
  });

  useEffect(() => {
    if (status?.status === 'approved') {
      const timer = setTimeout(() => {
        resetDraft();
        navigate('/tasks', { replace: true });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status?.status, navigate, resetDraft]);

  const backToProfile = () => navigate('/profile');

  // FTR-1452: перепроверка владения профилем по кнопке.
  // Текст ответа приходит с сервера и НЕ дублируется здесь: тот же текст
  // человек уже получил в боте, и две копии одного вердикта разъедутся.
  const [rechecking, setRechecking] = useState(false);
  const [ownershipMessage, setOwnershipMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const handleRecheck = async () => {
    if (rechecking) return;
    setRechecking(true);
    try {
      const res = await recheckOwnership();
      if (res.retry_after_sec != null) {
        // Бюджет попыток исчерпан — это НЕ вердикт о владении. Проверка
        // в TikTok платная, поэтому попытки ограничены.
        const mins = Math.max(1, Math.ceil(res.retry_after_sec / 60));
        setOwnershipMessage(`Слишком много проверок подряд. Попробуйте через ${mins} мин.`);
      } else {
        setOwnershipMessage(res.message ?? null);
        if (res.outcome === 'ok') {
          await queryClient.invalidateQueries({ queryKey: ['creator', 'verify_status'] });
        }
      }
    } catch {
      setOwnershipMessage('Не получилось проверить. Попробуйте ещё раз через пару минут.');
    } finally {
      setRechecking(false);
    }
  };

  // ── Rate-limited (EC-5) ───────────────────────────────────────────────
  if (isRateLimited && retryAfterMs !== null) {
    const timeStr = formatRetryAfter(retryAfterMs);
    const canRetryNow = retryAfterMs <= Date.now();
    return (
      <WizardFrame>
        <WizardHeader title="Заявка" onBack={backToProfile} />
        <WizardStepStrip current={8} total={8} />
        <WizardBody style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 16, padding: '24px 32px' }}>
          <div
            style={{
              width: 64, height: 64, borderRadius: 32, background: 'rgba(45,37,24,0.06)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Ic name="clock" size={32} color={P.warn} />
          </div>
          <div style={{ font: T.title3, color: P.ink }}>Слишком много попыток</div>
          <div style={{ font: T.subhead, color: P.inkSoft, lineHeight: 1.5 }}>
            {canRetryNow ? 'Можно подать заявку снова' : `Попробуй снова через ${timeStr}`}
          </div>
        </WizardBody>
        {canRetryNow && (
          <WizardCtaBar
            label="Подать заново"
            onClick={() => { resetDraft(); navigate('/creator-onboarding/url', { replace: true }); }}
          />
        )}
      </WizardFrame>
    );
  }

  // ── Approved ──────────────────────────────────────────────────────────
  if (status?.status === 'approved') {
    return (
      <WizardFrame>
        <WizardHeader title="Заявка одобрена" onBack={backToProfile} />
        <WizardStepStrip current={8} total={8} />
        <WizardBody style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 16, padding: '24px 32px' }}>
          <div
            style={{
              width: 72, height: 72, borderRadius: 36, background: '#EFF7E9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 0 8px rgba(45,138,78,0.10)',
            }}
          >
            <Ic name="check" size={36} color={P.success} />
          </div>
          <div style={{ font: T.title2, color: P.ink }}>Заявка одобрена!</div>
          <div style={{ font: T.subhead, color: P.inkSoft }}>Переходим к заданиям…</div>
        </WizardBody>
      </WizardFrame>
    );
  }

  // ── Rejected ──────────────────────────────────────────────────────────
  if (status?.status === 'rejected') {
    return (
      <WizardFrame>
        <WizardHeader title="Заявка" onBack={backToProfile} />
        <WizardStepStrip current={8} total={8} />
        <WizardBody>
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 16 }}>
            <div
              style={{
                width: 64, height: 64, borderRadius: 32, background: 'rgba(209,67,67,0.10)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Ic name="cross" size={28} color={P.error} />
            </div>
          </div>
          <div style={{ font: T.title2, color: P.ink, textAlign: 'center' }}>
            Заявка отклонена
          </div>
          {status.rejection_reason && (
            <div
              style={{
                borderRadius: 12,
                background: P.terraSoft,
                border: `1px solid ${P.terraLight}`,
                padding: '14px 16px',
              }}
            >
              <div style={{ font: T.subhead, color: P.ink, lineHeight: 1.5 }}>
                {rejectionReasonLabel(status.rejection_reason)}
              </div>
            </div>
          )}
          <div style={{ font: T.footnote, color: P.inkSoft, textAlign: 'center' }}>
            Вы можете подать заявку повторно
          </div>
        </WizardBody>
        <WizardCtaBar
          label="Подать снова"
          onClick={() => { resetDraft(); navigate('/creator-onboarding/url', { replace: true }); }}
        />
      </WizardFrame>
    );
  }

  // ── needs_info — ручная очередь оператора (BUG-1451 B1) ───────────────
  // Это НЕ спиннер: заявка вышла из автоматического конвейера и ждёт человека.
  // Опрос оставлен включённым, чтобы вердикт оператора долетел без перезагрузки.
  if (status?.status === 'needs_info') {
    // FTR-1452: заявка ждёт не оператора, а действия самого человека —
    // подписаться на сообщество либо вписать код в описание профиля. Кнопка
    // перепроверки показывается ТОЛЬКО в этом случае: во всех остальных
    // needs_info нажимать её бессмысленно, решает человек на нашей стороне.
    const isOwnership = (status.rejection_reason ?? '').startsWith('ownership_');
    // TECH-1456: заявка вышла в ручную очередь из-за отвала распознавателя.
    // Человеку причина не называется — только спокойная правда про сроки.
    const isVisionUnavailable = status.rejection_reason === 'vision_unavailable';
    return (
      <WizardFrame>
        <WizardHeader title="Заявка" onBack={backToProfile} />
        <WizardStepStrip current={8} total={8} />
        <WizardBody style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 16, padding: '24px 32px' }}>
          <div
            style={{
              width: 64, height: 64, borderRadius: 32, background: 'rgba(45,37,24,0.06)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Ic name="clock" size={32} color={P.warn} />
          </div>
          <div style={{ font: T.title3, color: P.ink }}>
            {isOwnership
              ? 'Подтвердите, что страница ваша'
              : isVisionUnavailable
                ? 'Ваш скрин ушёл на проверку'
                : 'Заявка на ручной проверке'}
          </div>
          <div style={{ font: T.subhead, color: P.inkSoft, lineHeight: 1.5 }}>
            {ownershipMessage
              ?? (isOwnership
                ? 'Мы прислали в бот, что нужно сделать. После этого нажмите «Проверить ещё раз».'
                : isVisionUnavailable
                  ? MANUAL_REVIEW_NOTICE
                  : 'Заявку смотрит оператор — обычно до 1–2 рабочих дней. Мы напишем в боте, как только будет результат.')}
          </div>
        </WizardBody>
        {isOwnership ? (
          <WizardCtaBar
            label={rechecking ? 'Проверяем…' : 'Проверить ещё раз'}
            onClick={handleRecheck}
          />
        ) : (
          <WizardCtaBar label="Закрыть" kind="ink" onClick={backToProfile} />
        )}
      </WizardFrame>
    );
  }

  // ── banned — терминальный (BUG-1451 B1) ───────────────────────────────
  // Без кнопки «Подать снова»: повторная подача бессмысленна и сожжёт суточную
  // корзину лимита. Единственное честное действие — выйти.
  if (status?.status === 'banned') {
    return (
      <WizardFrame>
        <WizardHeader title="Заявка" onBack={backToProfile} />
        <WizardStepStrip current={8} total={8} />
        <WizardBody style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 16, padding: '24px 32px' }}>
          <div
            style={{
              width: 64, height: 64, borderRadius: 32, background: 'rgba(209,67,67,0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Ic name="cross" size={28} color={P.error} />
          </div>
          <div style={{ font: T.title3, color: P.ink }}>Доступ закрыт</div>
          <div style={{ font: T.subhead, color: P.inkSoft, lineHeight: 1.5 }}>
            {status.rejection_reason
              ? rejectionReasonLabel(status.rejection_reason)
              : 'Подать заявку повторно нельзя. Если считаете это ошибкой — напишите в поддержку.'}
          </div>
        </WizardBody>
        <WizardCtaBar label="Закрыть" kind="ink" onClick={backToProfile} />
      </WizardFrame>
    );
  }

  // ── Незнакомый статус (BUG-1451 B1) ───────────────────────────────────
  // Спиннер обещает «1–2 минуты» и потому лжёт для всего, что не в работе.
  // Новый статус в бэкенде не должен превращаться в вечное вращение.
  if (status?.status && !SPINNER_STATUSES.includes(status.status)) {
    return (
      <WizardFrame>
        <WizardHeader title="Заявка" onBack={backToProfile} />
        <WizardStepStrip current={8} total={8} />
        <WizardBody style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 16, padding: '24px 32px' }}>
          <div
            style={{
              width: 64, height: 64, borderRadius: 32, background: 'rgba(45,37,24,0.06)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Ic name="clock" size={32} color={P.inkSoft} />
          </div>
          <div style={{ font: T.title3, color: P.ink }}>Статус уточняется</div>
          <div style={{ font: T.subhead, color: P.inkSoft, lineHeight: 1.5 }}>
            Мы напишем в боте, как только будет результат.
          </div>
        </WizardBody>
        <WizardCtaBar label="Закрыть" kind="ink" onClick={backToProfile} />
      </WizardFrame>
    );
  }

  // ── Pending (default) ─────────────────────────────────────────────────
  return (
    <WizardFrame>
      <WizardHeader title="Заявка на проверке" onBack={backToProfile} />
      <WizardStepStrip current={8} total={8} />
      <WizardBody style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 14, padding: '24px 32px' }}>
        <div
          style={{
            width: 56, height: 56, borderRadius: '50%',
            border: `3px solid ${P.terracotta}`,
            borderTopColor: 'transparent',
            animation: 'spin 0.9s linear infinite',
          }}
        />
        <div style={{ font: T.title3, color: P.ink }}>Проверяем заявку</div>
        <div style={{ font: T.subhead, color: P.inkSoft, lineHeight: 1.5 }}>
          Обычно занимает 1–2 минуты. Если автоматическая проверка не сработает,
          заявку рассмотрят вручную — это до 1–2 рабочих дней. Мы уведомим вас
          в боте.
        </div>
      </WizardBody>
      <WizardCtaBar label="Закрыть" kind="ink" onClick={backToProfile} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </WizardFrame>
  );
}
